import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useResolveJurisdictionMutation } from "graphql/mutations/resolve-jurisdiction.generated";
import { StandardizedAddressInput } from "graphql/generated/graphql";

/**
 * Which AHJ applies, worked out from the property's address and what is being
 * filed, so nobody starting an inspection or a plan review has to know which fire
 * marshal covers an address — or risk filing under the wrong one.
 *
 * The hook owns the field's value, not just the suggestion, because the two are
 * entangled: the lookup fills the field in, a manual pick overrules it, and
 * choosing a different property makes the whole question new again.
 */

export interface ResolvedJurisdictionAlternative {
	jurisdiction_id?: string | null;
	jurisdiction_name?: string | null;
	reason?: string | null;
}

export interface JurisdictionSuggestion {
	resolved: boolean;
	jurisdictionId: string | null;
	jurisdictionName: string | null;
	/** boundary | administrative | manual */
	matchMethod: string | null;
	/** high | medium | low */
	confidence: string | null;
	/** The lookup answered, but the answer deserves a second look before filing. */
	needsConfirmation: boolean;
	/** ROOFTOP | RANGE_INTERPOLATED | APPROXIMATE */
	geocodeLocationType: string | null;
	distanceToBoundaryMeters: number | null;
	alternatives: ResolvedJurisdictionAlternative[];
	message: string | null;
}

export interface UseResolvedJurisdictionArgs {
	/** A saved property. */
	propertyId?: string;
	/** Address of a property still being staged in the same form. */
	address?: string;
	standardizedAddress?: StandardizedAddressInput | null;
	inspectionType?: string;
	submissionTypeId?: string;
	/**
	 * An authority user files only for their own AHJ, so there is nothing to look
	 * up and nothing to choose.
	 */
	lockedJurisdictionId?: string;
	/**
	 * Whether the form has everything the answer depends on.
	 *
	 * The two flows differ and cannot share one rule. An inspection knows its list of
	 * types up front, so it waits for one to be picked — asking before that would
	 * resolve against a default and then have to change its own answer in front of
	 * the user. Plan review cannot wait: its submittal types are per-AHJ, so the AHJ
	 * has to be resolved before there is a type to pick, and the lookup re-runs on
	 * its own once one is.
	 */
	enabled?: boolean;
}

// Left on in every environment on purpose. The answer depends on the address, on
// which polygons are loaded and on which of them have an authority behind them —
// none of which is visible from the screen, so a report of "it picked the wrong
// one" is unactionable without this.
const AHJ_LOG = "[AHJ]";

export const useResolvedJurisdiction = ({
	propertyId,
	address,
	standardizedAddress,
	inspectionType,
	submissionTypeId,
	lockedJurisdictionId,
	enabled = true
}: UseResolvedJurisdictionArgs) => {
	const [chosenJurisdictionId, setChosenJurisdictionId] = useState<string>("");
	const [wasChosenByUser, setWasChosenByUser] = useState(false);
	const [suggestion, setSuggestion] = useState<JurisdictionSuggestion | null>(null);

	const [resolveJurisdiction, { loading }] = useResolveJurisdictionMutation({
		fetchPolicy: "no-cache"
	});

	// What the lookup is being asked about. Everything keys off this: a change is a
	// new question, so the previous answer and any manual pick stop applying.
	const lookupQuestionKey = useMemo(
		() => [propertyId ?? "", address?.trim() ?? "", inspectionType ?? "", submissionTypeId ?? ""].join("|"),
		[propertyId, address, inspectionType, submissionTypeId]
	);

	// Only the property changing invalidates a manual pick — switching the
	// inspection type is a refinement of the same question, and overruling the
	// user's correction there would be maddening.
	const propertyBeingAskedAbout = propertyId || address?.trim() || "";
	const previousPropertyAskedAbout = useRef(propertyBeingAskedAbout);

	useEffect(() => {
		if (previousPropertyAskedAbout.current === propertyBeingAskedAbout) return;
		console.log(`${AHJ_LOG} new property in the form, starting the question over:`, propertyBeingAskedAbout || "(cleared)");
		previousPropertyAskedAbout.current = propertyBeingAskedAbout;
		setWasChosenByUser(false);
		setChosenJurisdictionId("");
		setSuggestion(null);
	}, [propertyBeingAskedAbout]);

	useEffect(() => {
		if (lockedJurisdictionId) {
			console.log(`${AHJ_LOG} locked to the user's own authority:`, lockedJurisdictionId);

			return;
		}

		// Nothing to ask about yet, or the form is still missing something the answer
		// depends on. Asking anyway would resolve against a default and then correct
		// itself on screen, which reads as the system changing its mind.
		if (!enabled) return;
		if (!propertyId && !address?.trim()) return;

		// Only the answer to the question currently on screen is allowed to land. The
		// inputs are all dropdowns and a saved form, so two lookups overlapping means
		// the user changed their mind mid-flight; the earlier answer is stale by then.
		let answerStillWanted = true;
		const askedAtMs = Date.now();

		console.log(`${AHJ_LOG} asking:`, {
			propertyId: propertyId || null,
			address: propertyId ? null : address?.trim() || null,
			inspectionType: inspectionType || null,
			submissionTypeId: submissionTypeId || null
		});

		resolveJurisdiction({
			variables: {
				input: {
					property_id: propertyId || undefined,
					address: propertyId ? undefined : address || undefined,
					standardized_address: propertyId ? undefined : standardizedAddress || undefined,
					inspection_type: inspectionType || undefined,
					submission_type_id: submissionTypeId || undefined
				}
			}
		})
			.then(({ data }) => {
				const lookupResult = data?.resolve_jurisdiction;

				console.log(`${AHJ_LOG} answer in ${Date.now() - askedAtMs}ms:`, {
					resolved: lookupResult?.resolved,
					jurisdiction: lookupResult?.jurisdiction_name,
					matchMethod: lookupResult?.match_method,
					confidence: lookupResult?.confidence,
					needsConfirmation: lookupResult?.needs_confirmation,
					workKind: lookupResult?.work_kind,
					geocode: lookupResult?.geocode_location_type,
					metresFromEdge: lookupResult?.distance_to_boundary_meters,
					// Every area the address falls inside, whatever its kind. An empty
					// list means no boundary layer covers this address at all.
					areasCoveringTheAddress: (lookupResult?.context ?? []).map(
						area =>
							`${area?.type_name}: ${area?.name} -> ${area?.published_authority_name ?? "(no AHJ)"} [${
								area?.authority_source ?? "none"
							}]`
					),
					alternatives: (lookupResult?.alternatives ?? []).map(one => one?.jurisdiction_name),
					message: lookupResult?.message,
					error: lookupResult?.error ?? null,
					stillOnScreen: answerStillWanted
				});

				if (!answerStillWanted || !lookupResult) return;

				setSuggestion({
					resolved: !!lookupResult.resolved,
					jurisdictionId: lookupResult.jurisdiction_id ?? null,
					jurisdictionName: lookupResult.jurisdiction_name ?? null,
					matchMethod: lookupResult.match_method ?? null,
					confidence: lookupResult.confidence ?? null,
					needsConfirmation: !!lookupResult.needs_confirmation,
					geocodeLocationType: lookupResult.geocode_location_type ?? null,
					distanceToBoundaryMeters: lookupResult.distance_to_boundary_meters ?? null,
					alternatives: (lookupResult.alternatives ?? []) as ResolvedJurisdictionAlternative[],
					message: lookupResult.message ?? null
				});
			})
			.catch(error => {
				// The dropdown still works; a failed lookup just means filling it in.
				console.error(`${AHJ_LOG} lookup failed:`, error);
				if (answerStillWanted) setSuggestion(null);
			});

		return () => {
			answerStillWanted = false;
		};
		// Deliberately keyed on the question and not on every value that answers it:
		// standardizedAddress is a fresh object on each render, so depending on its
		// identity would re-run the lookup forever.
	}, [lookupQuestionKey, lockedJurisdictionId, enabled, resolveJurisdiction]);

	// Apply what the lookup found, unless somebody has overruled it.
	useEffect(() => {
		if (wasChosenByUser || lockedJurisdictionId) return;
		if (!suggestion?.resolved || !suggestion.jurisdictionId) return;

		console.log(
			`${AHJ_LOG} filling the field with`,
			suggestion.jurisdictionName,
			`(${suggestion.matchMethod}/${suggestion.confidence})`
		);

		setChosenJurisdictionId(suggestion.jurisdictionId);
	}, [suggestion, wasChosenByUser, lockedJurisdictionId]);

	const selectJurisdiction = useCallback(
		(jurisdictionId: string) => {
			console.log(`${AHJ_LOG} the user picked`, jurisdictionId, "over", suggestion?.jurisdictionId ?? "(nothing suggested)");
			setChosenJurisdictionId(jurisdictionId);
			setWasChosenByUser(true);
		},
		[suggestion]
	);

	const applySuggestion = useCallback(() => {
		setWasChosenByUser(false);
		if (suggestion?.jurisdictionId) setChosenJurisdictionId(suggestion.jurisdictionId);
	}, [suggestion]);

	const jurisdictionId = lockedJurisdictionId || chosenJurisdictionId;

	return {
		jurisdictionId,
		selectJurisdiction,
		applySuggestion,
		suggestion,
		loading,
		/** The value on screen came from the lookup rather than from a person. */
		isAutoSelected: !lockedJurisdictionId && !wasChosenByUser && !!chosenJurisdictionId,
		/** Nothing to look up or choose: the user files for one AHJ only. */
		isLocked: !!lockedJurisdictionId
	};
};
